/**
 * Live date-input mask helper.
 *
 * Formats raw text into a dot-separated date while the user types, so tablet
 * users only need the digit keys — the separators are inserted automatically.
 *
 * The strict date adapters (CustomDateAdapter / MonthYearDateAdapter) remain the
 * source of truth for parsing; this helper only produces a well-formed string
 * (`DD.MM.YYYY` / `MM.YYYY`) for them to parse.
 */

export interface DateMaskResult {
  value: string;
  caret: number;
}

/** Digit-count boundaries after which a separator dot is inserted (DD.MM.YYYY). */
export const DATE_MASK_BOUNDARIES = [2, 4] as const;
export const DATE_MASK_MAX_DIGITS = 8;

/** Digit-count boundaries after which a separator dot is inserted (MM.YYYY). */
export const MONTH_YEAR_MASK_BOUNDARIES = [2] as const;
export const MONTH_YEAR_MASK_MAX_DIGITS = 6;

function isDigit(charCode: number): boolean {
  return charCode >= 48 && charCode <= 57;
}

/**
 * Format a raw input value into a dot-separated date mask.
 *
 * Strips all non-digits, caps to `maxDigits`, and inserts a `.` after each
 * boundary digit count — but only BETWEEN groups (never a trailing dot), so
 * backspacing a completed group does not immediately re-add the separator.
 * The caret is recomputed so it stays next to the same digit it was before.
 *
 * @param raw        Current input value (may contain dots or other characters).
 * @param caret      Current caret position within `raw`.
 * @param boundaries Digit counts after which to insert a dot (e.g. `[2, 4]`).
 * @param maxDigits  Maximum number of digits to keep (e.g. `8`).
 */
export function formatDateMask(
  raw: string,
  caret: number,
  boundaries: readonly number[],
  maxDigits: number
): DateMaskResult {
  // 1. How many digits precede the old caret in the raw string.
  let digitsBeforeCaret = 0;
  const upTo = Math.min(caret, raw.length);
  for (let i = 0; i < upTo; i++) {
    if (isDigit(raw.charCodeAt(i))) {
      digitsBeforeCaret++;
    }
  }

  // 2. Strip non-digits and cap the length.
  const digits = raw.replace(/\D/g, '').slice(0, maxDigits);
  if (digitsBeforeCaret > digits.length) {
    digitsBeforeCaret = digits.length;
  }

  // 3. Rebuild with dots only BETWEEN groups (no trailing dot).
  const boundarySet = new Set(boundaries);
  let formatted = '';
  for (let i = 0; i < digits.length; i++) {
    formatted += digits[i];
    if (boundarySet.has(i + 1) && i + 1 < digits.length) {
      formatted += '.';
    }
  }

  // 4. Recompute the caret: walk until `digitsBeforeCaret` digits have passed.
  let newCaret = 0;
  if (digitsBeforeCaret > 0) {
    let seen = 0;
    for (let i = 0; i < formatted.length; i++) {
      if (isDigit(formatted.charCodeAt(i))) {
        seen++;
        if (seen === digitsBeforeCaret) {
          newCaret = i + 1;
          break;
        }
      }
    }
    // If the caret would land right before a separator, step past it so that
    // forward typing continues after the dot.
    if (newCaret < formatted.length && formatted[newCaret] === '.') {
      newCaret++;
    }
  }

  return { value: formatted, caret: newCaret };
}
